import React, { useMemo, useEffect } from 'react';
import { styled } from '@compiled/react';
import Select from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { defaultSelectStyles } from '@atlassian/jira-issue-field-select-base/src/ui/react-select-styles/styled.tsx';
import type { TransformedRole } from '../../common/types.tsx';
import useFetchRoles from '../../services/fetch-roles/index.tsx';
import messages from './messages.tsx';
import type { Props, SingleSelectItem, CurrentUser } from './types.tsx';
import {
	getOptionLabel,
	getOptionValue,
	convertRolesToCurrentRole,
	transformRolesToArrayIfNull,
	isUserRoleIncludedInRestrictedRoles,
} from './utils.tsx';

export const CurrentUserRoleElement = (props: CurrentUser) => {
	const { formatMessage } = useIntl();

	const { currentUserRole } = props;
	return currentUserRole ? (
		<FlexRow>
			<CurrentUserRole>{formatMessage(messages.currentUserRole)}</CurrentUserRole>
		</FlexRow>
	) : null;
};

// @ts-expect-error - TS7031 - Binding element 'context' implicitly has an 'any' type.
const formatOptionLabel = (option: SingleSelectItem, { context }) => {
	if (context === 'menu') {
		return (
			<FlexBody>
				<FlexColumn>
					<RoleName>{option.name}</RoleName>
					<SelectDescription>{option.description}</SelectDescription>
				</FlexColumn>
				<CurrentUserRoleElement currentUserRole={!!option.currentUserRole} />
			</FlexBody>
		);
	}
	return option.name;
};

export const RolePickerEdit = (props: Props) => {
	const {
		classNamePrefix,
		selectedRoles,
		selectedProjectId,
		onChange,
		isDisabled,
		onFocus,
		onBlur,
		autoFocus,
		isInvalid,
		onCloseMenuOnScroll,
		isDropdownMenuFixedAndLayered,
		setAccessWarningDisplay,
		ariaLabel,
		...fieldProps
	} = props;
	const { formatMessage } = useIntl();
	const { data: allRoles } = useFetchRoles(selectedProjectId);
	const rolesWithCurrentUserAccess = useMemo(
		() => allRoles.filter(({ currentUserRole }) => currentUserRole === true),
		[allRoles],
	);
	const shouldShowIssueAccessWarning = useMemo(
		() => !isUserRoleIncludedInRestrictedRoles(selectedRoles, rolesWithCurrentUserAccess),
		[rolesWithCurrentUserAccess, selectedRoles],
	);
	useEffect(() => {
		if (setAccessWarningDisplay) {
			setAccessWarningDisplay(shouldShowIssueAccessWarning);
		}
	}, [setAccessWarningDisplay, shouldShowIssueAccessWarning]);

	return (
		<Select
			{...fieldProps}
			classNamePrefix={classNamePrefix}
			// eslint-disable-next-line @atlassian/eng-health/code-evolution/ts-migration-4.9-5.4 -- ERROR TO BE FIXED
			// @ts-expect-error([Part of upgrade 4.9-5.4]) - Type 'Data' is not assignable to type 'readonly (SingleSelectItem | GroupBase<SingleSelectItem>)[]'.
			options={allRoles}
			isMulti
			onBlur={onBlur}
			onFocus={onFocus}
			formatOptionLabel={formatOptionLabel}
			menuPosition={isDropdownMenuFixedAndLayered === true ? 'fixed' : undefined}
			styles={defaultSelectStyles}
			isSearchable={false}
			placeholder={formatMessage(messages.placeholder)}
			// eslint-disable-next-line @atlassian/eng-health/code-evolution/ts-migration-4.9-5.4 -- ERROR TO BE FIXED
			// @ts-expect-error([Part of upgrade 4.9-5.4]) - Type '(role: TransformedRole) => string' is not assignable to type 'GetOptionValue<SingleSelectItem>'.
			getOptionValue={getOptionValue}
			// eslint-disable-next-line @atlassian/eng-health/code-evolution/ts-migration-4.9-5.4 -- ERROR TO BE FIXED
			// @ts-expect-error([Part of upgrade 4.9-5.4]) - Type '(role: TransformedRole) => string' is not assignable to type 'GetOptionLabel<SingleSelectItem>'.
			getOptionLabel={getOptionLabel}
			// eslint-disable-next-line @atlassian/eng-health/code-evolution/ts-migration-4.9-5.4 -- ERROR TO BE FIXED
			// @ts-expect-error([Part of upgrade 4.9-5.4]) - Type 'TransformedRole[]' is not assignable to type 'PropsValue<SingleSelectItem> | undefined'.
			value={convertRolesToCurrentRole(selectedRoles, allRoles)}
			// @ts-expect-error - TS2322 - Type '(roles: TransformedRole[]) => void' is not assignable to type '(value: OptionsType<TransformedRole>, action: ActionMeta<TransformedRole>) => void'.
			onChange={(roles: TransformedRole[]) => transformRolesToArrayIfNull(roles, onChange)}
			isDisabled={isDisabled}
			autoFocus={autoFocus}
			// @ts-expect-error - TS2322 - Type '"error" | null' is not assignable to type 'ValidationState | undefined'.
			validationState={isInvalid === true ? 'error' : null}
			closeMenuOnScroll={onCloseMenuOnScroll}
			aria-label={ariaLabel}
			aria-labelledby={fieldProps['aria-labelledby']}
		/>
	);
};

export default RolePickerEdit;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectDescription = styled.div({
	whiteSpace: 'initial',

	font: token('font.body.small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexBody = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexColumn = styled.div({
	flexDirection: 'column',
	display: 'flex',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/design-system/no-empty-styled-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RoleName = styled.div({});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexRow = styled.div({
	display: 'flex',
	alignItems: 'center',
	paddingLeft: token('space.250'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const CurrentUserRole = styled.div({
	fontStyle: 'italic',
});
